import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Divider from "../components/Divider";
import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import BossSectionComponent from "./dashboard/components/BossSectionComponent";
import { useAuth } from "../contexts/AuthContext";
import NotFound from "./NotFound";
import Loading from "../components/Loading";

const API_URL = process.env.REACT_APP_API_URL;
const IMG_URL = process.env.REACT_APP_IMG_URL;

const PatchDetails = ({ initialPatch }) => {
  const { user } = useAuth();
  const { patchNo } = useParams(); 
  const { language, t } = useLanguage();

  const [patch, setPatch] = useState(initialPatch ?? null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPatch(initialPatch);
  }, [initialPatch]);

  useEffect(() => {
    if (patchNo) {
      fetch(`${API_URL}/${language}/patchnote/${patchNo}`, {
        headers: { 
            Authorization: `Bearer ${
                JSON.parse(localStorage.getItem("user"))?.token
            }` 
        }
      })
        .then((res) => res.json())
        .then((data) => setPatch(data))
        .catch((err) => setError(err));
    }
  }, [patchNo, language]);

  const formattedDate = new Date(patch?.tarih).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });


  if (error) return <NotFound message={t("failedtoloadpatch")} />;

  if (!patch) return <Loading />;

  if (patch.message) return <NotFound message={t(patch.message)} />;

  if (!patch.isPublished && !user) return <NotFound />;

  if (patch)
    return (
      <motion.div
        key={patch.yamaNo}
        className="mt-4 break-words"
        initial={{ opacity: 0, filter: "blur(10px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        exit={{ opacity: 0, filter: "blur(6px)" }}
        transition={{ duration: 0.5 }}
      >
        <div className="relative flex justify-center">
          <img
            className="flex jsutify-center w-max md:h-[600px] md:w-full items-center rounded-[30px] bg-white relative z-10 blur-sm opacity-50"
            src={
              patch.image?.includes("uploads")
                ? IMG_URL + patch.image
                : patch.image
            }
            alt={patch.title}
          />
          <img
            src={
              patch.image?.includes("uploads")
                ? IMG_URL + patch.image
                : patch.image
            }
            alt={patch.yamaNo}
            className="m-auto object-cover w-max md:h-[600px] md:w-[1200px] rounded-[30px] z-10 absolute top-0 mt-12"
            style={{ backgroundImage: "urk" }}
          />
        </div>
        <div
          className={
            "py-12 w-full leading-loose " +
            (!initialPatch
              ? "whitespace-pre-line px-4 sm:px-12 md:px-28 lg:px-48 2xl:px-72"
              : "px-2")
          }
        >
          <p className="text-end mt-12 mx-4 sm:mx-0">{formattedDate}</p>
          <div className="border-t-2 mx-4 sm:mx-0">
            <h1 className="text-3xl font-bold my-12">{patch.title}</h1>
            <h2 className="text-xl font-semibold my-12">{patch.subtitle}</h2>
            <h3 className="text-lg font-semibold my-6">{t("patchsummary")}</h3>
            <p className="italic text-sm leading-loose px-6 sm:p-0">
              "{patch.summary}"
            </p>

            <Divider />
            {/* <img
                        className="rounded-xl my-24 mx-auto p-2 border border-amber-500 shadow-2xl shadow-amber-100/40"
                        src={`data:image/png;base64,${patch.image}`}
                        alt={patch.title}
                    /> */}
            <div className="space-y-36 mt-36 -mx-4 sm:mx-0">
              {patch.sections?.map((sec, index) =>
                sec.bossUpdate ? (
                  <BossSectionComponent
                    key={index}
                    patch={patch}
                    sec={sec}
                    index={index}
                    initialPatch={initialPatch}
                  />
                ) : (
                  <div key={index} id={sec.id}>
                    <div className="flex items-center">
                      <h4 className="text-xl text-start font-semibold mb-6 break-all">
                        {initialPatch && index + 1 + ". Bölüm: "}
                        {sec.title}
                      </h4>
                    </div>
                    <div className="p-4 md:p-12 text-start border border-gray-600 rounded-md">
                      {sec?.thumbnail?.length > 0 ? (
                        <div key={sec.id} className="w-full">
                          <img
                            className="mx-auto h-48 w-auto md:h-48 md:object-contain md:float-start md:mr-8 mb-2"
                            src={
                              sec.thumbnail[0]?.includes("uploads")
                                ? IMG_URL + sec.thumbnail
                                : sec.thumbnail
                            }
                            alt={patch.title}
                          />
                          <div className="break-words">
                            <div
                              className="block"
                              style={{ color: "white" }}
                              dangerouslySetInnerHTML={{
                                __html: sec.content
                                  .replace(/\s+/g, " ")
                                  .replace(/\n/g, ""),
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div key={sec.id} className="break-words">
                          <div
                            className="block"
                            style={{ color: "white", backgroundColor: "transparent" }}
                            dangerouslySetInnerHTML={{
                              __html: sec.content
                                ?.replace(/\s+/g, " ")
                                .replace(/\n/g, "")
                                .replace(/background(-color)?:\s*[^;"]+/gi, 'background-color: transparent'),
                            }}
                          />
                        </div>
                      )}

                      {sec.images &&
                        sec.images.map((img, index) => (
                          <img
                            key={index}
                            className="mt-24 rounded-2xl"
                            src={img?.includes("uploads") ? IMG_URL + img : img}
                            alt={patch.title}
                          />
                        ))}
                    </div>
                    {sec.table && (
                      <div className="overflow-x-auto mt-6 pb-4">
                        <table className="min-w-full border-collapse">
                          <thead>
                            <tr>
                              {sec.table.columns.map((col, idx) => (
                                <th
                                  key={idx}
                                  className="border p-2 text-center bg-gray-600 bg-opacity-40 whitespace-nowrap"
                                >
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sec.table.rows.map((row, idx) => (
                              <tr key={idx}>
                                {row.map((cell, cellIdx) => (
                                  <td
                                    key={cellIdx}
                                    className="border py-2 px-4 whitespace-nowrap"
                                  >
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <Divider />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
};

export default PatchDetails;
